import type { CommissionType } from '../types/multiInfluencer';
import { getCampaignPassthroughTotal, getCampaignDeductionsTotal } from './campaignExpenses';

export type { CommissionType };

export interface CreatorCommissionData {
  agreed_fee: number;
  commission_pct: number;
  commission_flat_fee: number;
  commission_type: CommissionType;
}

/**
 * The money-relevant columns of a campaign, as either a raw database row or a
 * computed campaign. Every helper below accepts this shape so raw rows (KPIs,
 * reports, exports) and computed rows (table, payment views) agree.
 */
export interface CampaignMoneyFields {
  campaign_mode?: string | null;
  has_payables?: boolean | null;
  fee?: number | null;
  commission_pct?: number | null;
  commission_flat_fee?: number | null;
  commission_type?: string | null;
  /** Present on computed campaigns; preferred when available. */
  commission_amount?: number;
  campaign_expenses?: unknown;
  /** Sponsorships keep their value and agency cut here, with fee/commission_pct at 0. */
  sponsorship_amount?: number | string | null;
  agency_commission_percent?: number | string | null;
}

export function calculateInfluencerCommission(
  agreedFee: number,
  commissionPct: number,
  commissionFlatFee: number,
  commissionType: CommissionType
): number {
  if (commissionType === 'flat_fee') {
    return commissionFlatFee || 0;
  }
  return agreedFee * commissionPct;
}

export function calculateInfluencerNetPay(
  agreedFee: number,
  commissionPct: number,
  commissionFlatFee: number,
  commissionType: CommissionType
): number {
  const commission = calculateInfluencerCommission(agreedFee, commissionPct, commissionFlatFee, commissionType);
  return agreedFee - commission;
}

export function sumCreatorCommissions(creators: CreatorCommissionData[]): number {
  return creators.reduce((total, c) => {
    return total + calculateInfluencerCommission(
      c.agreed_fee || 0,
      c.commission_pct ?? 0,
      c.commission_flat_fee ?? 0,
      c.commission_type ?? 'percentage'
    );
  }, 0);
}

export function sumCreatorFees(creators: CreatorCommissionData[]): number {
  return creators.reduce((total, c) => total + (c.agreed_fee || 0), 0);
}

export function isSponsorshipCampaign(campaign: { campaign_mode?: string | null }): boolean {
  return campaign.campaign_mode === 'sponsorship';
}

/**
 * True when a campaign is revenue only: a service delivered with no supplier
 * cost, so nothing is paid out and the whole fee is Mohit's margin.
 * Only services opt out of payables, and the column defaults to true, so
 * every other campaign (and anything predating the flag) is unaffected.
 */
export function isRevenueOnlyCampaign(campaign: {
  campaign_mode?: string | null;
  has_payables?: boolean | null;
}): boolean {
  return campaign.campaign_mode === 'services' && campaign.has_payables === false;
}

/**
 * The fee the campaign is priced on. Sponsorships store it in
 * sponsorship_amount (the row's fee is 0); computed sponsorship rows also copy
 * it into fee, so either shape resolves to the same number.
 */
export function getCampaignBaseFee(campaign: CampaignMoneyFields): number {
  if (isSponsorshipCampaign(campaign)) {
    return Number(campaign.sponsorship_amount) || Number(campaign.fee) || 0;
  }
  return Number(campaign.fee) || 0;
}

/** The percentage that applies to the base fee (sponsorships use the agency cut). */
export function getCampaignCommissionPct(campaign: CampaignMoneyFields): number {
  if (isSponsorshipCampaign(campaign)) {
    return Number(campaign.agency_commission_percent) || 0;
  }
  return Number(campaign.commission_pct) || 0;
}

/**
 * The part of the Valor the commission applies to. The Valor typed on a
 * campaign already includes any pass-through expense (it is what the client
 * pays); that part goes to the creator untouched, so it is left out here.
 */
export function getCommissionableFee(campaign: CampaignMoneyFields): number {
  return Math.max(0, getCampaignBaseFee(campaign) - getCampaignPassthroughTotal(campaign));
}

/**
 * Agency commission for a whole campaign: percentage or flat fee on the
 * Valor minus pass-through, the agency cut for sponsorships, the entire fee
 * for revenue-only services.
 */
export function computeCampaignCommission(campaign: CampaignMoneyFields): number {
  const baseFee = getCampaignBaseFee(campaign);
  if (isRevenueOnlyCampaign(campaign)) return baseFee;
  const commissionable = getCommissionableFee(campaign);
  if (isSponsorshipCampaign(campaign)) {
    return commissionable * getCampaignCommissionPct(campaign);
  }
  return calculateInfluencerCommission(
    commissionable,
    getCampaignCommissionPct(campaign),
    Number(campaign.commission_flat_fee) || 0,
    (campaign.commission_type as CommissionType) || 'percentage'
  );
}

/** Commission to use for a campaign, preferring the already-computed figure. */
export function getCampaignCommissionAmount(campaign: CampaignMoneyFields): number {
  return typeof campaign.commission_amount === 'number'
    ? campaign.commission_amount
    : computeCampaignCommission(campaign);
}

/**
 * What the client is invoiced: the Valor, which already includes any
 * pass-through expense (Andamente 2026/124: 17 539,70 € with 539,70 € of
 * expenses inside — not 18 079,40 €).
 */
export function getCampaignTotalValue(campaign: CampaignMoneyFields): number {
  return getCampaignBaseFee(campaign);
}

/**
 * What the creator ends up with over the whole campaign: the Valor minus
 * commission (so the pass-through part reaches them in full), minus any
 * deductions we carry on their behalf.
 */
export function getCreatorTotalDue(campaign: CampaignMoneyFields): number {
  const commission = getCampaignCommissionAmount(campaign);
  return Math.max(
    0,
    getCampaignTotalValue(campaign) - commission - getCampaignDeductionsTotal(campaign)
  );
}

/**
 * The part of a campaign's commission that one invoice stands for: the
 * invoice's share of the total, applied to the commission. Null when it
 * can't be worked out (no total, no amount or no commission).
 */
export function commissionShare(
  amount: number,
  total: number,
  commission: number
): { value: number; share: number } | null {
  if (!(amount > 0) || !(total > 0) || !(commission > 0)) return null;
  const share = amount / total;
  return { value: Math.round(commission * share * 100) / 100, share };
}
